import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery as useConvexQuery, useMutation as useConvexMutation } from "convex/react";
import { api } from "../../api";
import { ConvexErrorBoundary } from "./ConvexErrorBoundary";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Check, ArrowLeft, Search, Globe, X, AlertTriangle } from "lucide-react";
import { AppIcon } from "./AppIcon";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import type { GenericId } from "convex/values";

// Wildcard app ID - URLs approved should be added to the app's webkit and synced to wildcard
const WILDCARD_APP_ID = "j57fgp9z6fejkw5frve4bnx1957m90gc";

// Type for webkit request from API
type WebkitRequest = {
  _id: GenericId<"webkitRequests">;
  _creationTime: number;
  url: string;
  app: string;
  serial: string;
  status: "pending" | "approved" | "denied";
};

const getStatusBadgeVariant = (status: WebkitRequest["status"]) => {
  switch (status) {
    case "pending":
      return "outline";
    case "approved":
      return "default";
    case "denied":
      return "destructive";
    default:
      return "outline";
  }
};

const getStatusBadgeClassName = (status: WebkitRequest["status"]) => {
  switch (status) {
    case "pending":
      return "border-amber-500 text-amber-600 bg-amber-50";
    case "approved":
      return "bg-success text-success-foreground";
    case "denied":
      return "bg-destructive text-destructive-foreground";
    default:
      return "";
  }
};

// Apps Content Component
const VerifiedAppsContent = () => {
  const navigate = useNavigate();
  const [verifiedAppIds, setVerifiedAppIds] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");

  const appsData = useConvexQuery(api.apps.list.all, {
    paginationOpts: {
      cursor: null,
      numItems: 100,
    },
  });

  const categories = useConvexQuery(api.appCategories.list.categories);
  const updateAppCategory = useConvexMutation(api.apps.update.category);
  const updateAppVerified = useConvexMutation(api.apps.update.verified);

  const handleVerify = async (appId: string, appName: string) => {
    try {
      setVerifiedAppIds(prev => new Set([...prev, appId]));
      await updateAppVerified({
        app: appId as any,
        verified: true,
      });
      toast.success(`${appName} has been verified`);
    } catch (error) {
      setVerifiedAppIds(prev => {
        const next = new Set(prev);
        next.delete(appId);
        return next;
      });
      toast.error("Failed to verify app");
      console.error(error);
    }
  };

  const handleCategoryChange = async (appId: string, category: string, appName: string) => {
    try {
      await updateAppCategory({
        app: appId as any,
        category,
      });
      toast.success(`${appName} category updated to ${category}`);
    } catch (error) {
      toast.error("Failed to update app category");
      console.error(error);
    }
  };

  if (!appsData || !categories) {
    return (
      <Card className="p-6">
        <div className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      </Card>
    );
  }

  const unverifiedApps = appsData.page.filter((app: any) => {
    if (verifiedAppIds.has(app.id)) return false;
    const isUnverified = app.verified === undefined || app.verified === null || app.verified === false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesName = app.name?.toLowerCase().includes(search);
      const matchesBundleId = app.bundleId?.toLowerCase().includes(search);
      return isUnverified && (matchesName || matchesBundleId);
    }
    return isUnverified;
  });

  return (
    <div className="space-y-4">
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search apps..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9 h-8"
        />
      </div>

      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-b sticky top-0 bg-background z-10">
                <TableHead className="w-[40px] text-xs font-medium bg-background py-2">Icon</TableHead>
                <TableHead className="text-xs font-medium bg-background py-2">App Name</TableHead>
                <TableHead className="text-xs font-medium bg-background py-2">Category</TableHead>
                <TableHead className="text-xs font-medium w-[70px] bg-background py-2">Type</TableHead>
                <TableHead className="text-right text-xs font-medium w-[90px] bg-background py-2">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {unverifiedApps.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <div className="text-muted-foreground">
                      <Check className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p className="font-medium text-sm">No unverified apps found</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                unverifiedApps.map((app: any) => (
                  <TableRow key={app.id} className="hover:bg-muted/50">
                    <TableCell className="py-1.5 px-3">
                      <AppIcon app_icon_url={app.iconUrl} app_name={app.name} size="sm" />
                    </TableCell>
                    <TableCell className="text-sm py-1.5 px-3">{app.name}</TableCell>
                    <TableCell className="py-1.5 px-3">
                      <Select
                        value={app.category || "Uncategorized"}
                        onValueChange={(value) => handleCategoryChange(app.id, value, app.name)}
                      >
                        <SelectTrigger className="w-[160px] h-8 text-xs">
                          <SelectValue placeholder="Uncategorized" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Uncategorized">Uncategorized</SelectItem>
                          {categories.map((cat) => (
                            <SelectItem key={cat.name} value={cat.name}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="py-1.5 px-3">
                      <Badge 
                        variant={app.system ? "outline" : "secondary"}
                        className="font-normal text-xs px-1.5 py-0"
                      >
                        {app.system ? "System" : "Store"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right py-1.5 px-3">
                      {verifiedAppIds.has(app.id) ? (
                        <div className="flex items-center justify-end gap-1 text-success">
                          <Check className="w-3 h-3" />
                          <span className="text-xs font-medium">Verified</span>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleVerify(app.id, app.name)}
                          className="bg-success hover:bg-success/80 text-success-foreground border-success h-7 gap-1 text-xs px-2"
                        >
                          <Check className="w-3 h-3" />
                          Verify
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};

// Websites Content Component
const VerifiedWebsitesContent = () => {
  const [statusFilter, setStatusFilter] = useState<"pending" | "approved" | "denied" | undefined>("pending");
  const [searchTerm, setSearchTerm] = useState("");
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());

  // Paginated query for webkit requests
  const requestsData = useConvexQuery(api.webkitRequests.list.all, {
    paginationOpts: {
      cursor: null,
      numItems: 100,
    },
    status: statusFilter,
  });

  // Fetch wildcard app data to get current webkit URLs
  const wildcardAppData = useConvexQuery(api.apps.get.apps, {
    apps: [WILDCARD_APP_ID as any],
  });
  const wildcardUrls: string[] = (wildcardAppData?.[0] as any)?.webkit || [];

  const updateStatus = useConvexMutation(api.webkitRequests.update.status);
  const updateAppWebkit = useConvexMutation(api.apps.update.webkit);

  // Normalize URL for webkit list (extract domain/path pattern)
  const normalizeUrlForWebkit = (url: string): string => {
    try {
      const urlObj = new URL(url);
      // Return the hostname as the webkit pattern
      return urlObj.hostname;
    } catch {
      // If URL parsing fails, return as-is
      return url;
    }
  };

  const handleApprove = async (request: WebkitRequest) => {
    if (processingIds.has(request._id)) return;

    try {
      setProcessingIds(prev => new Set([...prev, request._id]));

      // 1. Mark as approved
      await updateStatus({
        id: request._id,
        status: "approved",
      });

      const normalizedUrl = normalizeUrlForWebkit(request.url).trim().toLowerCase();

      // 2. Add URL to wildcard app only (merge, don't replace)
      try {
        if (!wildcardUrls.includes(normalizedUrl)) {
          await updateAppWebkit({
            app: WILDCARD_APP_ID as any,
            webkit: [...wildcardUrls, normalizedUrl],
          });
          console.log("URL added to wildcard app:", normalizedUrl);
        }
      } catch (webkitError) {
        console.warn("Could not update wildcard webkit list:", webkitError);
      }

      toast.success(`Approved: ${request.url}`);
    } catch (error) {
      console.error("Failed to approve request:", error);
      toast.error("Failed to approve request");
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(request._id);
        return next;
      });
    }
  };

  const handleDeny = async (request: WebkitRequest) => {
    if (processingIds.has(request._id)) return;

    try {
      setProcessingIds(prev => new Set([...prev, request._id]));

      await updateStatus({
        id: request._id,
        status: "denied",
      });

      toast.success(`Denied: ${request.url}`);
    } catch (error) {
      console.error("Failed to deny request:", error);
      toast.error("Failed to deny request");
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(request._id);
        return next;
      });
    }
  };

  if (!requestsData) {
    return (
      <Card className="p-6">
        <div className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      </Card>
    );
  }

  // Filter by search term
  const filteredRequests = requestsData.page.filter((request: WebkitRequest) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      request.url.toLowerCase().includes(search) ||
      request.app.toLowerCase().includes(search) ||
      request.serial.toLowerCase().includes(search)
    );
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search URL, app, or serial..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-8"
          />
        </div>
        
        <Select
          value={statusFilter || "all"}
          onValueChange={(value) => 
            setStatusFilter(value === "all" ? undefined : value as "pending" | "approved" | "denied")
          }
        >
          <SelectTrigger className="w-[140px] h-8">
            <SelectValue placeholder="Filter status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="denied">Denied</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-b sticky top-0 bg-background z-10">
                <TableHead className="text-xs font-medium bg-background py-1.5">URL</TableHead>
                <TableHead className="text-xs font-medium bg-background w-[180px] py-1.5">App</TableHead>
                <TableHead className="text-xs font-medium bg-background w-[120px] py-1.5">Device</TableHead>
                <TableHead className="text-xs font-medium bg-background w-[80px] py-1.5">Status</TableHead>
                <TableHead className="text-xs font-medium bg-background w-[100px] py-1.5">Date</TableHead>
                <TableHead className="text-right text-xs font-medium w-[120px] bg-background py-1.5">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="text-muted-foreground">
                      <Globe className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p className="font-medium text-sm">
                        {statusFilter === "pending" 
                          ? "No pending website requests" 
                          : "No website requests found"
                        }
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRequests.map((request: WebkitRequest) => {
                  const isProcessing = processingIds.has(request._id);
                  
                  return (
                    <TableRow key={request._id} className="hover:bg-muted/50">
                      <TableCell className="py-1 px-3">
                        <span className="text-xs truncate block max-w-[280px]" title={request.url}>
                          {request.url}
                        </span>
                      </TableCell>
                      <TableCell className="py-1 px-3">
                        <span className="text-xs text-muted-foreground truncate block" title={request.app}>
                          {request.app}
                        </span>
                      </TableCell>
                      <TableCell className="py-1 px-3">
                        <span className="text-xs font-mono text-muted-foreground">
                          {request.serial}
                        </span>
                      </TableCell>
                      <TableCell className="py-1 px-3">
                        <Badge 
                          variant={getStatusBadgeVariant(request.status)}
                          className={`${getStatusBadgeClassName(request.status)} text-[10px] px-1.5 py-0`}
                        >
                          {request.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-1 px-3">
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(request._creationTime), "MMM d")}
                        </span>
                      </TableCell>
                      <TableCell className="text-right py-1 px-3">
                        {request.status === "pending" ? (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleApprove(request)}
                              disabled={isProcessing}
                              className="bg-success hover:bg-success/80 text-success-foreground border-success h-6 gap-1 text-[11px] px-2"
                            >
                              <Check className="w-3 h-3" />
                              Approve
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeny(request)}
                              disabled={isProcessing}
                              className="bg-destructive hover:bg-destructive/80 text-destructive-foreground border-destructive h-6 gap-1 text-[11px] px-2"
                            >
                              <X className="w-3 h-3" />
                              Deny
                            </Button>
                          </div>
                        ) : (
                          <span className={`text-[11px] font-medium ${request.status === "approved" ? "text-success" : "text-destructive"}`}>
                            {request.status === "approved" ? "Approved" : "Denied"}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};

// Error fallback for websites section
const WebsitesErrorFallback = ({ error, resetError }: { error?: Error; resetError: () => void }) => {
  return (
    <Card className="p-6">
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Unable to load website requests</AlertTitle>
        <AlertDescription>
          There was an error loading the webkit requests. This may be a temporary backend issue.
          Please try refreshing the page or contact support if the problem persists.
        </AlertDescription>
      </Alert>
    </Card>
  );
};

// Main Section Component
export const VerifiedSection = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "apps";

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  return (
    <div className="px-6">
      {/* Header with back button */}
      <div className="flex items-center gap-4 mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/section/apps")}
          className="flex items-center gap-2 -ml-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="apps">Apps</TabsTrigger>
          <TabsTrigger value="websites">Websites</TabsTrigger>
        </TabsList>

        <TabsContent value="apps">
          <VerifiedAppsContent />
        </TabsContent>

        <TabsContent value="websites">
          <ConvexErrorBoundary fallback={WebsitesErrorFallback}>
            <VerifiedWebsitesContent />
          </ConvexErrorBoundary>
        </TabsContent>
      </Tabs>
    </div>
  );
};